<template>
    <ul class="flex flex-col">
        <li v-for="moment in moments" :key="moment.id" class="row-item">
            <button
                type="button"
                class="group flex w-full items-center gap-3 rounded-md px-2.5 py-3.5 text-start transition-colors enabled:hover:bg-valence-cream-50 disabled:cursor-default disabled:opacity-60"
                :disabled="moment.state === 'locked'"
                :data-testid="`picker-moment-${moment.id}`"
                @click="emit('select', moment.id)">
                <span class="grow min-w-0 body-small text-valence-stone-900 truncate">{{ moment.title }}</span>
                <span class="flex items-center gap-3 shrink-0">
                    <StatusChip size="2xs" :state="chipState(moment)" class="min-w-24 justify-center">
                        {{ chipLabel(moment) }}
                    </StatusChip>
                    <i
                        class="bi bi-arrow-right text-sm/none text-valence-stone-900 opacity-0 -translate-x-1 transition-[opacity,translate] duration-150 group-hover:opacity-100 group-hover:translate-x-0"
                        aria-hidden="true"></i>
                </span>
            </button>
        </li>
        <li class="row-item">
            <button
                type="button"
                class="flex w-full items-center gap-3 rounded-md px-2.5 py-3.5 text-start transition-colors hover:bg-valence-cream-50"
                data-testid="picker-moment-something-else"
                @click="emit('dismiss')">
                <span class="flex items-center justify-center p-1 rounded-md bg-valence-cream-50 shrink-0">
                    <i class="bi bi-pencil text-base/none text-valence-stone-700" aria-hidden="true"></i>
                </span>
                <span class="scaffold-body-small text-valence-stone-500">Something else</span>
            </button>
        </li>
    </ul>
</template>

<script setup lang="ts">
import StatusChip from "~vue/components/StatusChip.vue";
import type { PerformanceMomentSummary } from "~vue/types/performance_management";

defineProps<{ moments: PerformanceMomentSummary[] }>();

const emit = defineEmits<{ select: [momentId: number]; dismiss: [] }>();

const chipState = (moment: PerformanceMomentSummary) => {
    if (moment.state === "expired") {
        return "neutral";
    }
    if (moment.aggregate_status === "completed") {
        return "done";
    }
    return moment.aggregate_status === "in_progress" ? "active" : "neutral";
};

const chipLabel = (moment: PerformanceMomentSummary): string => {
    if (moment.state === "locked") {
        return "Coming soon";
    }
    if (moment.state === "expired") {
        return "Expired";
    }
    if (moment.aggregate_status === "completed") {
        return "Complete";
    }
    return moment.aggregate_status === "in_progress" ? "In progress" : "Not started";
};
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .row-item {
        @apply border-b border-valence-cream-200 transition-colors;
    }

    .row-item:last-child {
        @apply border-b-0;
    }
}

/* The hairline touching a hovered row fades out (unlayered so it beats the border utility). */
.row-item:hover,
.row-item:has(+ .row-item:hover) {
    border-color: transparent;
}
</style>
